import React, { createContext } from 'react';
import { navigate } from 'gatsby';
import { CardElement } from '@stripe/react-stripe-js';
import isStrongPassword from 'validator/lib/isStrongPassword';

import { withFirebase } from '../Firebase';
import { withUser } from '../Session/context';
import { FormContext } from '../Form/context';
import { withCart } from '../Cart/context';
import { passwordStrength } from '../PasswordChange/errors';

import StoreData from '../../data/store';
import { calculateDeposit, calculateVat } from './calculations';

import {
  ERROR_CODE_ACCOUNT_EXISTS,
  ERROR_MSG_ACCOUNT_EXISTS,
} from '../Register/errors';

const initialState = {
  totals: [],
  shipping: false,
  discount: false,
  processing: false,
  error: false,
  success: false,
  accountSuccess: false,
  order_data: null,
  payment: 0,
  preOrder: false,
  deposit: false,
};

export const CheckoutContext = createContext(initialState);

class CheckoutProvider extends React.Component {
  _isMounted = false;

  _userActive = false;

  constructor(props) {
    super(props);
    this.state = {
      ...initialState,
    };
    this.processOrder = this.processOrder.bind(this);
    this.initUser = this.initUser.bind(this);
    this.checkoutError = this.checkoutError.bind(this);
    this.checkoutSuccess = this.checkoutSuccess.bind(this);
    this.completeOrder = this.completeOrder.bind(this);
    this.saveFirebaseOrder = this.saveFirebaseOrder.bind(this);
    this.createAccount = this.createAccount.bind(this);
    this.createOrder = this.createOrder.bind(this);
    this.saveForLater = this.saveForLater.bind(this);
    this.saveAddress = this.saveAddress.bind(this);
    this.validateCheckout = this.validateCheckout.bind(this);
    this.calculateTotals = this.calculateTotals.bind(this);
    this.setShipping = this.setShipping.bind(this);
    this.setDiscount = this.setDiscount.bind(this);
    this.togglePayment = this.togglePayment.bind(this);
    this.toggleDeposit = this.toggleDeposit.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.calculateTotals();
    this.initUser();
  }

  componentDidUpdate(prevProps) {
    const { authUser } = this.props;
    if (authUser !== prevProps.authUser && !this._userActive) {
      this.initUser();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._userActive = false;
  }

  /* Calculates checkout totals */
  calculateTotals = () => {
    const { total, items, authUser } = this.props;
    const { shipping, discount, deposit } = this.state;

    /* Get sub total */
    const subTotal = parseFloat(Math.round(total * 100) / 100);

    /* Get VAT amount */
    const priceVat = calculateVat(subTotal);
    /* If logged in add VAT to subtotal */
    const vatTotal = authUser !== null ? subTotal + priceVat : subTotal;

    /* Get shipping total */
    const shippingTotal = parseFloat(
      Math.round((shipping.total ? shipping.total : 0) * 100) / 100,
    );

    /* Calculate deposit + remaining amount */
    const { remaining, depositTotal } = calculateDeposit(vatTotal);

    /* Get coupon discount */
    const discountAmount = this.discountAmount(discount, activeTotal);

    /* Calculate grand total */
    const activeTotal = deposit ? depositTotal : vatTotal;
    const grandTotal = parseFloat(
      Math.round((activeTotal - discountAmount + shippingTotal) * 100) / 100,
    );

    const totalData = [
      { label: 'Subtotal', amount: subTotal },
      { label: 'VAT', amount: priceVat, hidden: authUser === null },
      {
        label: 'Discount',
        amount: discountAmount,
        hidden: discountAmount === 0,
      },
      { label: 'Shipping', amount: shippingTotal, hidden: authUser !== null },
      { label: 'Deposit', amount: depositTotal, hidden: !deposit },
      { label: 'On delivery', amount: remaining, hidden: !deposit },
      { label: 'Total today', amount: grandTotal },
    ];

    const preOrderItems = items?.some(
      x => x.stock_quantity < 1 && x.backorders,
    );

    if (this._isMounted) {
      this.setState({
        totals: totalData,
        preOrder: preOrderItems,
      });
    }
  };

  /* Set checkout shipping */
  setShipping = val => {
    if (this._isMounted) {
      this.setState({ shipping: val }, () => {
        this.calculateTotals();
      });
    }
  };

  /* Set coupon discount */
  setDiscount = val => {
    const { discount } = this.state;
    if (discount.free_shipping && !val) {
      this.setShipping(false);
    }
    if (this._isMounted) {
      this.setState({ discount: val }, () => {
        if (val.free_shipping) {
          this.setShipping({
            method_id: 'free_delivery',
            method_title: 'Free Delivery',
            total: '0',
          });
        } else {
          this.calculateTotals();
        }
      });
    }
  };

  /* Discount type */
  discountAmount = (discount, subTotal) => {
    if (discount.type === 'fixed_cart') {
      return parseFloat(Math.round(discount.amount * 100) / 100);
    }

    if (discount.type === 'percent') {
      return parseFloat(
        Math.round((subTotal / 100) * discount.amount * 100) / 100,
      );
    }

    return 0;
  };

  /* Toggle payment method */
  togglePayment = (e, id) => {
    e.preventDefault();
    if (this._isMounted) {
      this.setState({ payment: id });
    }
  };

  /*  Autofill checkout fields with users details either from RTDB or passed down from application form */
  initUser = () => {
    const { authUser } = this.props;
    const { bulkChange } = this.context;

    if (!this._userActive) {
      this._userActive = true;
      if (authUser) {
        const { email, username, address } = authUser;
        bulkChange({
          authUser: true,
          email_address: email,
          first_name: username.split(' ')[0],
          last_name: username.split(' ')[1],
          street_address: address ? address.street_address : '',
          city: address ? address.city : '',
          county: address ? address.county : '',
          post_code: address ? address.post_code : '',
          phone_number: address ? address.phone_number : '',
          country: address ? address.country : '',
        });
        /* If logged in auto set free shipping */
        this.setShipping({
          method_title: 'Free Delivery',
          method_id: 'free_shipping',
          total: '0',
        });
      } else {
        bulkChange({ authUser: false });
      }
    }
  };

  /* Display any error messages */
  checkoutError = error => {
    this.setState({ processing: false, error }, () => {
      window.scrollTo({ top: 0, left: 0 });
    });
  };

  /* Display any success messages */
  checkoutSuccess = (message = '') => {
    this.setState({ processing: false, error: false, success: message }, () => {
      window.scrollTo({ top: 0, left: 0 });
    });
  };

  /* Start order process using card payment method */
  processOrder = e => {
    e.preventDefault();
    if (this._isMounted) {
      this.setState({ processing: true }, async () => {
        const { values } = this.context;
        const { payment } = this.state;

        const orderMethod =
          payment === 0 ? this.createOrder : this.saveForLater;

        if (values.create_account) {
          // const isCompany = await StoreData.validateCompany(
          //   values.company_number,
          // );

          // if (!isCompany.error) {
          return this.createAccount(orderMethod);
          // }

          // return this.checkoutError(
          //   `You must enter a valid company number to create a trade account.`,
          // );
        }

        return orderMethod();
      });
    }
  };

  /* Complete order and navigate to confirmation page */
  completeOrder = (data, save) => {
    const { id, billing, shipping, date_created, total } = data;

    const url = save ? `/checkout/saved` : `/checkout/complete`;

    navigate(url, {
      state: {
        confirmation: {
          id,
          shipping,
          billing,
          date_created,
          total,
        },
      },
    });
  };

  /* Save order details to users account */
  saveFirebaseOrder = (res, items, address) => {
    const { firebase, authUser } = this.props;
    const { id, shipping, date_created, total } = res;
    const { pushDatabase, orders } = firebase;
    /* Save order information */
    const orderData = {
      id,
      shipping,
      date_created,
      total,
      items,
    };
    pushDatabase(orders(authUser.uid), orderData).then(() => {
      return address && this.saveAddress();
    });
  };

  /* Create an account during checkout */
  createAccount(callback) {
    const { firebase } = this.props;
    const { values } = this.context;
    const {
      doCreateUserWithEmailAndPassword,
      setDatabase,
      user,
      doSendEmailVerification,
    } = firebase;

    const {
      first_name,
      last_name,
      email_address,
      password,
      phone_number,
      street_address,
      city,
      county,
      post_code,
    } = values;

    const date = new Date().toJSON();
    const username = `${first_name} ${last_name}`;

    const address = {
      street_address,
      city,
      county,
      post_code,
      phone_number,
    };

    const checkPassword = isStrongPassword(password);

    if (!checkPassword) {
      this.checkoutError(passwordStrength);
    } else {
      doCreateUserWithEmailAndPassword(email_address, password)
        .then(authUser => {
          const userData = {
            created: date,
            username,
            email: email_address,
            address,
            preferences: {
              newsletter: true,
              notifications: true,
            },
          };
          setDatabase(user(authUser.user.uid), userData);
          return authUser.user;
        })
        .then(user => {
          return doSendEmailVerification(user);
        })
        .then(() => {
          if (this._isMounted) {
            this.setState({ accountSuccess: true });
          }
        })
        .catch(error => {
          if (error.code === ERROR_CODE_ACCOUNT_EXISTS) {
            error.message = ERROR_MSG_ACCOUNT_EXISTS;
          }
          this.checkoutError(error.message);
        })
        .finally(() => callback());
    }
  }

  /* Process stripe order */
  async createOrder() {
    let paymentIntent;

    const { items, authUser } = this.props;
    const { payment, shipping, discount, deposit, totals } = this.state;
    const { values } = this.context;

    const orderData = {
      ...values,
      payment,
      products: items,
      shipping,
      discount,
      meta: authUser,
      totals,
      deposit,
    };
    // Create blank order
    StoreData.createOrder(orderData)
      .then(async res => {
        if (this._isMounted) {
          this.setState({ order_data: res });
        }

        if (!res.error) {
          // Create Payment Intent for stripe
          paymentIntent = await StoreData.paymentIntent(res);
          // Charge card
          this.submitPayment(paymentIntent.client_secret);
        } else {
          this.checkoutError(res.message);
        }
      })
      .catch(error => {
        this.checkoutError(error.message);
      });
  }

  /* Save for later */
  async saveForLater() {
    const { items, authUser } = this.props;
    const { payment, shipping, discount, deposit, totals } = this.state;
    const { values } = this.context;

    const orderData = {
      ...values,
      payment,
      products: items,
      shipping,
      discount,
      meta: authUser,
      totals,
      deposit,
    };
    // Create blank order
    StoreData.createOrder(orderData)
      .then(async res => {
        if (this._isMounted) {
          this.setState({ order_data: res });
        }

        if (!res.error) {
          /* Save order data to user account in firebase */
          if (authUser) {
            this.saveFirebaseOrder(res, items, values.save_address);
          }
          return res;
        }

        return this.checkoutError(res.message);
      })
      .then(res => {
        if (!res.error) {
          this.completeOrder(res, true);
        }
      })
      .catch(error => {
        this.checkoutError(error.message);
      });
  }

  /* Capture stripe payment */
  async submitPayment(secret) {
    const { stripe, elements, items, authUser } = this.props;
    const { values } = this.context;

    const {
      first_name,
      last_name,
      email_address,
      phone,
      street_address,
      city,
      county,
      post_code,
    } = values;

    const cardElement = elements.getElement(CardElement);

    stripe.createToken(cardElement);

    const result = await stripe.confirmCardPayment(secret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: `${first_name} ${last_name}`,
          email: email_address,
          phone,
          address: {
            line1: street_address,
            city,
            state: county,
            postal_code: post_code,
            country: 'GB',
          },
        },
      },
    });

    if (result.error) {
      this.checkoutError('There was an error processing your payment.');
    } else {
      const paymentIntent = await result.paymentIntent;
      const { order_data } = this.state;
      StoreData.updateOrder(order_data.id, paymentIntent.id)
        .then(res => {
          /* Save order data to user account in firebase */
          if (authUser) {
            this.saveFirebaseOrder(res, items, values.save_address);
          }
          return res;
        })
        .then(res => this.completeOrder(res));
    }
  }

  /* Save address details to user account */
  saveAddress() {
    const { values } = this.context;
    const { firebase, authUser } = this.props;
    const { street_address, city, county, post_code, phone_number } = values;
    const { setDatabase, address } = firebase;

    setDatabase(address(authUser.uid), {
      street_address,
      city,
      county,
      post_code,
      phone_number,
    }).catch(error => {
      this.checkoutError(error.message);
    });
  }

  /* Check if checkout is valid */
  validateCheckout() {
    const { values } = this.context;

    const requiredFields = [
      'first_name',
      'last_name',
      'street_address',
      'city',
      'county',
      'post_code',
      'phone_number',
      'terms_conditions',
    ];

    if (values.create_account) {
      requiredFields.push('password');
    }

    const isDisabled = requiredFields.some(
      x => values[x] === undefined || values[x] === '' || values[x] === false,
    );

    return isDisabled;
  }

  /* Toggle full or deposit payment */
  toggleDeposit() {
    if (this._isMounted) {
      const { deposit } = this.state;
      this.setState({ deposit: !deposit }, () => {
        this.calculateTotals();
      });
    }
  }

  render() {
    const { children } = this.props;
    const {
      processing,
      error,
      success,
      accountSuccess,
      totals,
      shipping,
      discount,
      payment,
      preOrder,
      deposit,
    } = this.state;

    const checkoutData = {
      checkout: {
        processing,
        error,
        success,
        accountSuccess,
        totals,
        shipping,
        discount,
        payment,
        preOrder,
        deposit,
      },
      processOrder: this.processOrder,
      createOrder: this.createOrder,
      validateCheckout: this.validateCheckout,
      checkoutError: this.checkoutError,
      checkoutSuccess: this.checkoutSuccess,
      setDiscount: this.setDiscount,
      setShipping: this.setShipping,
      togglePayment: this.togglePayment,
      toggleDeposit: this.toggleDeposit,
    };
    return (
      <CheckoutContext.Provider value={checkoutData}>
        {children}
      </CheckoutContext.Provider>
    );
  }
}

export default withFirebase(withUser(withCart(CheckoutProvider)));

CheckoutProvider.contextType = FormContext;
